/**
 * Pure helpers for Food Safety setup forms (create / edit payloads).
 */

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  FOOD_SAFETY_ADHOC_SECTIONS,
  FOOD_SAFETY_ADHOC_TEMP_DEFAULTS,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_DELIVERY_TEMP_PRESETS,
  FOOD_SAFETY_DISHWASHER_DEFAULTS,
  FOOD_SAFETY_FREQUENCY,
  FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES,
  FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY,
  FOOD_SAFETY_SCHEDULE_MODE,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_DEFAULTS,
  FOOD_SAFETY_SETUP_SECTIONS,
  FOOD_SAFETY_TASK_KIND,
} from 'src/constants/foodSafety'
import {
  inferDeliveryTempBand,
  resolveNewDeliveryTempConfig,
} from 'src/utils/foodSafetyAdHoc'
import {
  formatProbeTestSummary,
  isDishwasherUnit,
  isProbeBothTests,
  probeTestPreset,
  validateProbeSetupFields,
} from 'src/utils/foodSafetyProbe'
import { getVenueRoleRefusalCopy } from 'src/utils/venueRoleCapabilities'
import {
  formatFoodSafetyTempRange,
  formatTempValue,
} from 'src/utils/foodSafetyTempRange'
import { normalizeFoodSafetyTimeText } from 'src/utils/foodSafetyTime'

/** Setup blocked state — same words as the Express 403. */
export const foodSafetyManageBlockedCopy = (role, customRoles = []) =>
  `You do not have permission to manage Food Safety setup. ${getVenueRoleRefusalCopy(
    role,
    customRoles,
  )}`

const DELIVERY_TEMP_DEFAULTS =
  FOOD_SAFETY_ADHOC_TEMP_DEFAULTS[FOOD_SAFETY_SECTION.DELIVERIES] || {}

export function fridgeDueTimesFromForm(form) {
  const raw =
    Array.isArray(form?.dueTimes) && form.dueTimes.length
      ? form.dueTimes
      : [form?.dueByTime]
  const times = []
  const seen = new Set()
  for (const value of raw) {
    const time = normalizeFoodSafetyTimeText(value)
    if (!time || seen.has(time)) continue
    seen.add(time)
    times.push(time)
  }
  return times.slice(0, FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES)
}

const slugStepKey = (label, index) => {
  const base = String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40)
  return base || `step_${index + 1}`
}

const isProbeSection = section =>
  [
    FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
    FOOD_SAFETY_SECTION.DISHWASHERS,
  ].includes(section)

/**
 * Scheduled or ad hoc for this form. Probes are ad hoc unless Setup turns on
 * "Check on a schedule"; every other section follows its default.
 */
export function resolveSetupScheduleMode(form) {
  if (isProbeSection(form?.section)) {
    return form.probeScheduled
      ? FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED
      : FOOD_SAFETY_SCHEDULE_MODE.AD_HOC
  }
  return FOOD_SAFETY_SECTION_DEFAULTS[form?.section]?.scheduleMode || null
}

/** Draft patch for the probe "Check on a schedule" toggle. */
export function setupProbeSchedulePatch(form, scheduled) {
  return scheduled
    ? {
        probeScheduled: true,
        dueByTime: form?.dueByTime || '10:00',
        frequency: form?.frequency || FOOD_SAFETY_FREQUENCY.DAILY,
      }
    : { probeScheduled: false }
}

const probeFormFields = config => {
  if (isDishwasherUnit(config)) {
    const preset = probeTestPreset()
    return {
      equipmentKind: 'dishwasher',
      probeReference: '',
      calibrationTest: preset.calibrationTest,
      referenceTemp:
        config?.referenceTemp ?? FOOD_SAFETY_DISHWASHER_DEFAULTS.wash.target,
      boilingReferenceTemp:
        config?.boilingReferenceTemp ??
        FOOD_SAFETY_DISHWASHER_DEFAULTS.rinse.target,
      tolerance: preset.tolerance,
    }
  }
  const preset = probeTestPreset(config?.calibrationTest)
  return {
    equipmentKind: 'probe',
    probeReference: config?.probeReference || '',
    calibrationTest: preset.calibrationTest,
    referenceTemp: config?.referenceTemp ?? preset.referenceTemp,
    boilingReferenceTemp:
      config?.boilingReferenceTemp ?? preset.boilingReferenceTemp ?? null,
    tolerance: config?.tolerance ?? preset.tolerance,
  }
}

export function blankSetupForm(section) {
  const defaults = FOOD_SAFETY_SECTION_DEFAULTS[section] || {}
  if (isProbeSection(section)) {
    return {
      ...blankSetupForm(null),
      section,
      probeScheduled: false,
      ...probeFormFields(
        section === FOOD_SAFETY_SECTION.DISHWASHERS
          ? { equipmentKind: 'dishwasher' }
          : {},
      ),
    }
  }
  return {
    section,
    name: '',
    dueByTime:
      defaults.scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED
        ? '10:00'
        : '',
    dueTimes: section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? ['10:00'] : [],
    frequency: defaults.frequency || FOOD_SAFETY_FREQUENCY.DAILY,
    scheduleStartsOn: '',
    weekdays: [],
    unitType: 'fridge',
    tempMin: section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? 0 : null,
    tempMax:
      section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS
        ? 5
        : section === FOOD_SAFETY_SECTION.DELIVERIES
        ? DELIVERY_TEMP_DEFAULTS.tempMax ?? 5
        : section === FOOD_SAFETY_SECTION.COOLING
        ? 8
        : null,
    cookingKind: 'cooking',
    cookingTempMin: 75,
    deliveryTempBand:
      section === FOOD_SAFETY_SECTION.DELIVERIES
        ? FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED
        : null,
    steps: [{ key: 'step_1', label: '' }],
    ...(section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS
      ? { taskKind: FOOD_SAFETY_TASK_KIND.CHECKLIST }
      : {}),
  }
}

/** A cleaning row with no kind is a checklist. */
export function resolveCleaningTaskKind(value) {
  return value?.taskKind === FOOD_SAFETY_TASK_KIND.JOB
    ? FOOD_SAFETY_TASK_KIND.JOB
    : FOOD_SAFETY_TASK_KIND.CHECKLIST
}

export function isCleaningJob(value) {
  return (
    value?.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS &&
    resolveCleaningTaskKind(value) === FOOD_SAFETY_TASK_KIND.JOB
  )
}

/** Add form prefilled from a unit: same range and schedule, blank name. */
export function duplicateSetupForm(definition) {
  return {
    ...formFromDefinition(definition),
    name: '',
  }
}

export function formFromDefinition(definition) {
  const config = definition?.config || {}
  const steps =
    Array.isArray(config.steps) && config.steps.length
      ? config.steps.map((s, i) => ({
          key: s.key || slugStepKey(s.label, i),
          label: s.label || '',
        }))
      : [{ key: 'step_1', label: '' }]

  return {
    section: definition.section,
    name: definition.name || '',
    dueByTime: definition.dueByTime || '',
    dueTimes:
      Array.isArray(definition.dueTimes) && definition.dueTimes.length
        ? [...definition.dueTimes]
        : definition.dueByTime
        ? [definition.dueByTime]
        : definition.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS
        ? ['10:00']
        : [],
    frequency: definition.frequency || FOOD_SAFETY_FREQUENCY.DAILY,
    scheduleStartsOn: definition.scheduleStartsOn || '',
    weekdays: Array.isArray(config.weekdays) ? [...config.weekdays] : [],
    unitType: config.unitType || 'fridge',
    // Deliveries are only checked against a maximum; an older saved minimum
    // is dropped when the template is next saved.
    tempMin:
      definition.section === FOOD_SAFETY_SECTION.DELIVERIES
        ? null
        : config.tempMin ?? null,
    tempMax: config.tempMax ?? null,
    cookingKind: config.cookingKind || 'cooking',
    cookingTempMin: config.tempMin ?? 75,
    deliveryTempBand:
      definition.section === FOOD_SAFETY_SECTION.DELIVERIES
        ? inferDeliveryTempBand(config)
        : null,
    steps,
    ...(definition.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS
      ? { taskKind: resolveCleaningTaskKind(definition) }
      : {}),
    ...(isProbeSection(definition.section)
      ? {
          probeScheduled:
            definition.scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED,
          ...probeFormFields(config),
        }
      : {}),
  }
}

/**
 * Validate form before API call. Returns error string or null.
 */
export function validateSetupForm(form) {
  if (!form?.name?.trim()) return 'Name is required.'
  const defaults = FOOD_SAFETY_SECTION_DEFAULTS[form.section]
  if (!defaults) return 'Choose a valid section.'

  if (resolveSetupScheduleMode(form) === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED) {
    if (form.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS) {
      const times = fridgeDueTimesFromForm(form)
      if (!times.length) {
        return 'Add at least one due time as HH:mm (e.g. 10:00).'
      }
      if (times.length > FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES) {
        return `Each fridge can have up to ${FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES} due times.`
      }
    } else if (!normalizeFoodSafetyTimeText(form.dueByTime)) {
      return 'Due-by time is required as HH:mm (e.g. 10:00).'
    }
    if (!form.frequency) return 'Frequency is required.'
    if (
      form.frequency === FOOD_SAFETY_FREQUENCY.WEEKLY &&
      (!form.weekdays || form.weekdays.length === 0)
    ) {
      return 'Pick at least one weekday for weekly checks.'
    }
  }

  if (
    form.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS &&
    !isCleaningJob(form)
  ) {
    const steps = (form.steps || []).filter(s => String(s.label || '').trim())
    if (steps.length < 1) return 'Add at least one checklist step.'
  }

  if (isProbeSection(form.section)) {
    const probeError = validateProbeSetupFields(form)
    if (probeError) return probeError
  }

  if (
    form.tempMin != null &&
    form.tempMax != null &&
    Number(form.tempMin) > Number(form.tempMax)
  ) {
    return 'Minimum temperature must be ≤ maximum.'
  }

  return null
}

/**
 * Build create/update body for Express.
 */
export function buildSetupPayload(form, { isCreate = true } = {}) {
  const scheduleMode = resolveSetupScheduleMode(form)
  const name = String(form.name || '').trim()
  const config = {}

  if (form.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS) {
    config.unitType = form.unitType || 'fridge'
    if (form.tempMin != null && form.tempMin !== '') {
      config.tempMin = Number(form.tempMin)
    }
    if (form.tempMax != null && form.tempMax !== '') {
      config.tempMax = Number(form.tempMax)
    }
  }

  if (form.section === FOOD_SAFETY_SECTION.COOKING_REHEATING) {
    config.cookingKind = form.cookingKind || 'cooking'
    if (form.cookingTempMin != null && form.cookingTempMin !== '') {
      config.tempMin = Number(form.cookingTempMin)
    }
  }

  if (form.section === FOOD_SAFETY_SECTION.COOLING) {
    if (form.tempMax != null && form.tempMax !== '') {
      config.tempMax = Number(form.tempMax)
    }
  }

  if (form.section === FOOD_SAFETY_SECTION.DELIVERIES) {
    const band =
      form.deliveryTempBand ||
      inferDeliveryTempBand({
        tempMin: form.tempMin,
        tempMax: form.tempMax,
      })
    if (band) config.tempBand = band
    // Ambient: no range. Chilled / frozen: a maximum only.
    if (
      band !== FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT &&
      form.tempMax != null &&
      form.tempMax !== ''
    ) {
      config.tempMax = Number(form.tempMax)
    }
  }

  if (form.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS) {
    if (isCleaningJob(form)) {
      config.steps = []
    } else {
      config.steps = (form.steps || [])
        .map((s, i) => ({
          key: s.key || slugStepKey(s.label, i),
          label: String(s.label || '').trim(),
        }))
        .filter(s => s.label)
    }
  }

  if (isProbeSection(form.section)) {
    if (isDishwasherUnit(form)) {
      config.equipmentKind = 'dishwasher'
      config.referenceTemp = Number(form.referenceTemp)
      config.boilingReferenceTemp = Number(form.boilingReferenceTemp)
    } else {
      const reference = String(form.probeReference || '').trim()
      if (reference) config.probeReference = reference
      config.calibrationTest = form.calibrationTest
      config.referenceTemp = Number(form.referenceTemp)
      if (isProbeBothTests(form)) {
        config.boilingReferenceTemp = Number(form.boilingReferenceTemp)
      }
      config.tolerance = Number(form.tolerance)
    }
  }

  if (
    scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED &&
    form.frequency === FOOD_SAFETY_FREQUENCY.WEEKLY &&
    Array.isArray(form.weekdays) &&
    form.weekdays.length
  ) {
    config.weekdays = form.weekdays.map(Number)
  }

  const body = {
    name,
    config,
  }

  if (isCreate) {
    body.section = form.section
  }

  if (form.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS) {
    body.taskKind = resolveCleaningTaskKind(form)
  }

  if (scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED) {
    if (form.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS) {
      const times = fridgeDueTimesFromForm(form)
      body.dueTimes = times
      body.dueByTime = times[0]
    } else {
      body.dueByTime =
        normalizeFoodSafetyTimeText(form.dueByTime) || form.dueByTime
    }
    body.frequency = form.frequency
    if (form.scheduleStartsOn) {
      body.scheduleStartsOn = form.scheduleStartsOn
    }
  }

  // Probes can switch between ad hoc and scheduled, so always say which.
  if (isProbeSection(form.section)) {
    body.scheduleMode = scheduleMode
    if (scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.AD_HOC && !isCreate) {
      body.frequency = null
      body.dueByTime = null
    }
  }

  return body
}

export function formatDefinitionSummary(definition) {
  const parts = []
  if (definition.scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED) {
    parts.push(definition.frequency || 'scheduled')
    if (
      definition.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS &&
      Array.isArray(definition.dueTimes) &&
      definition.dueTimes.length
    ) {
      parts.push(`due ${definition.dueTimes.join(', ')}`)
    } else if (definition.dueByTime) {
      parts.push(`due ${definition.dueByTime}`)
    }
  } else {
    parts.push('as you go')
  }
  const cfg = definition.config || {}
  if (isProbeSection(definition.section)) {
    if (cfg.probeReference) parts.push(cfg.probeReference)
    parts.push(formatProbeTestSummary(cfg))
    return parts.join(' · ')
  }
  if (cfg.unitType) parts.push(cfg.unitType)
  if (cfg.cookingKind) parts.push(cfg.cookingKind)
  // Deliveries: band / range lives on the list-card badge (formatSetupDeliveryBandLabel).
  if (definition.section !== FOOD_SAFETY_SECTION.DELIVERIES) {
    if (cfg.tempMin != null && cfg.tempMax != null) {
      parts.push(formatFoodSafetyTempRange(cfg.tempMin, cfg.tempMax))
    } else if (cfg.tempMin != null) {
      parts.push(`≥${formatTempValue(cfg.tempMin)}°C`)
    } else if (cfg.tempMax != null) {
      parts.push(`≤${formatTempValue(cfg.tempMax)}°C`)
    }
  }
  if (definition.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS) {
    const isJob =
      resolveCleaningTaskKind(definition) === FOOD_SAFETY_TASK_KIND.JOB
    parts.push(isJob ? 'Job' : 'Checklist')
    if (!isJob && Array.isArray(cfg.steps) && cfg.steps.length) {
      const count = cfg.steps.length
      parts.push(`${count} step${count === 1 ? '' : 's'}`)
    }
  } else if (Array.isArray(cfg.steps)) {
    parts.push(`${cfg.steps.length} steps`)
  }
  return parts.join(' · ')
}

/**
 * Glanceable delivery temp band for Setup list cards (19.31).
 * Prefers Chilled / Frozen / Ambient; falls back to a short °C range.
 */
export function formatSetupDeliveryBandLabel(definition) {
  if (definition?.section !== FOOD_SAFETY_SECTION.DELIVERIES) return null
  const cfg = definition.config || {}
  const band = inferDeliveryTempBand(cfg)
  if (band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
    return FOOD_SAFETY_DELIVERY_TEMP_PRESETS[
      FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT
    ].label
  }
  // What new readings are judged against: a maximum only (older minimums
  // no longer apply).
  const judged = resolveNewDeliveryTempConfig(cfg).tempMax
  const max = judged != null ? `≤${formatTempValue(judged)}°C` : null
  if (
    band === FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN ||
    band === FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED
  ) {
    const label = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band].label
    return max ? `${label} ${max}` : label
  }
  return max
}

function coerceSetupTempPart(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Short °C range for Setup delivery band chips / live preview (29.38). */
export function formatSetupDeliveryTempRangeLabel(tempMin, tempMax) {
  const min = coerceSetupTempPart(tempMin)
  const max = coerceSetupTempPart(tempMax)
  if (min != null && max != null) return formatFoodSafetyTempRange(min, max)
  if (min != null) return `≥${formatTempValue(min)}°C`
  if (max != null) return `≤${formatTempValue(max)}°C`
  return null
}

/**
 * Chip range suffix for Setup delivery band picker (29.38).
 * Ambient → "no temp". Selected (or inferred) band uses live form min/max;
 * other chips keep the preset so managers see each option before picking.
 */
export function formatSetupDeliveryBandChipRange(band, form) {
  if (band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
    return 'no temp'
  }
  const preset = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band]
  if (!preset) return null
  const selected = form?.deliveryTempBand
  const inferred =
    selected == null
      ? inferDeliveryTempBand({
          tempMin: form?.tempMin,
          tempMax: form?.tempMax,
        })
      : null
  const live = selected === band || inferred === band
  if (live) {
    return (
      formatSetupDeliveryTempRangeLabel(form?.tempMin, form?.tempMax) ||
      formatSetupDeliveryTempRangeLabel(preset.tempMin, preset.tempMax)
    )
  }
  return formatSetupDeliveryTempRangeLabel(preset.tempMin, preset.tempMax)
}

/**
 * Live “what you’ll save” preview beside the band chips (29.38).
 * Ambient → "no temp"; otherwise the form min/max as they type.
 */
export function formatSetupDeliveryBandLivePreview(form) {
  const band =
    form?.deliveryTempBand ||
    inferDeliveryTempBand({
      tempMin: form?.tempMin,
      tempMax: form?.tempMax,
    })
  if (band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
    return 'no temp'
  }
  return formatSetupDeliveryTempRangeLabel(form?.tempMin, form?.tempMax)
}

/** Short noun for the thing being added (kitchen language, not “definition”). */
export function setupAddButtonLabel(section) {
  switch (section) {
    case FOOD_SAFETY_SECTION.FRIDGES_FREEZERS:
      return 'Add fridge or freezer'
    case FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS:
      return 'Add job or checklist'
    case FOOD_SAFETY_SECTION.COOKING_REHEATING:
      return 'Add cooking shortcut'
    case FOOD_SAFETY_SECTION.COOLING:
      return 'Add cooling shortcut'
    case FOOD_SAFETY_SECTION.DELIVERIES:
      return 'Add delivery shortcut'
    case FOOD_SAFETY_SECTION.HOT_HOLDING:
      return 'Add hot holding shortcut'
    case FOOD_SAFETY_SECTION.COLD_HOLDING:
      return 'Add cold holding shortcut'
    case FOOD_SAFETY_SECTION.PROBE_CALIBRATION:
      return 'Add temperature probe'
    case FOOD_SAFETY_SECTION.DISHWASHERS:
      return 'Add dishwasher'
    default:
      return 'Add item'
  }
}

/** Create button in the drawer. A job is not saved as a checklist. A dishwasher is not saved as a probe. */
export function setupSaveButtonLabel(form, { editing = false } = {}) {
  if (editing) return 'Save changes'
  if (form?.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS) {
    return isCleaningJob(form) ? 'Add job' : 'Add checklist'
  }
  if (isProbeSection(form?.section)) {
    return isDishwasherUnit(form) ? 'Add dishwasher' : 'Add probe'
  }
  return setupAddButtonLabel(form?.section)
}

export function setupEmptyStateCopy(section) {
  switch (section) {
    case FOOD_SAFETY_SECTION.FRIDGES_FREEZERS:
      return {
        title: 'No fridges or freezers yet',
        body: 'Add each cold unit once — the kitchen logs temps against it every day.',
      }
    case FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS:
      return {
        title: 'No checklists yet',
        body: 'Add a closing or opening checklist so staff can tick it off on the day.',
      }
    case FOOD_SAFETY_SECTION.COOKING_REHEATING:
      return {
        title: 'No cooking shortcuts yet',
        body: 'Optional. Save dishes you cook often — tap them on the day to log faster. Staff can still type a name.',
      }
    case FOOD_SAFETY_SECTION.COOLING:
      return {
        title: 'No cooling shortcuts yet',
        body: 'Optional. Save batches you cool often — tap them on the day to log faster. Staff can still type a name.',
      }
    case FOOD_SAFETY_SECTION.DELIVERIES:
      return {
        title: 'No delivery shortcuts yet',
        body: 'Optional. Save products that arrive often — tap them on the day to log faster. Staff can still type a name.',
      }
    case FOOD_SAFETY_SECTION.HOT_HOLDING:
      return {
        title: 'No hot holding shortcuts yet',
        body: 'Optional. Save what you keep hot for service — tap them on the day to log faster. Staff can still type a name.',
      }
    case FOOD_SAFETY_SECTION.COLD_HOLDING:
      return {
        title: 'No cold holding shortcuts yet',
        body: 'Optional. Save what you keep cold on display — tap them on the day to log faster. Staff can still type a name.',
      }
    case FOOD_SAFETY_SECTION.PROBE_CALIBRATION:
      return {
        title: 'No temperature probes yet',
        body: 'Add each probe for ice-water and boiling-water calibration checks.',
      }
    case FOOD_SAFETY_SECTION.DISHWASHERS:
      return {
        title: 'No dishwashers yet',
        body: 'Add each dishwasher with its wash and final-rinse limits and its own checking schedule.',
      }
    default:
      return {
        title: 'Nothing here yet',
        body: 'Add one to get the kitchen logging.',
      }
  }
}

export function setupDrawerTitle({ editing, section } = {}) {
  if (editing?.name) return `Edit ${editing.name}`
  return setupAddButtonLabel(section)
}

export function setupSaveSuccessMessage({ isCreate, name } = {}) {
  const label = String(name || '').trim() || 'Item'
  return isCreate ? `${label} added` : `${label} updated`
}

export function isFoodSafetySetupSection(section) {
  return FOOD_SAFETY_SETUP_SECTIONS.includes(section)
}

/** Cooking, cooling, holding, and deliveries can be logged without setup. */
export function isFoodSafetyOptionalSetupSection(section) {
  return FOOD_SAFETY_ADHOC_SECTIONS.includes(section)
}

/**
 * First-run Setup tabs (29.70) — fridges + one checklist path only.
 * Cook / cool / delivery templates unlock after the first fridge save.
 */
export const FOOD_SAFETY_SETUP_FIRST_RUN_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS,
])

export const FOOD_SAFETY_SETUP_TEMPLATES_DEFERRED_HINT =
  'Cooking, cooling, holding, deliveries, and probes unlock after you add a fridge.'

export function hasFoodSafetyFridgeDefinition(definitions = []) {
  return (definitions || []).some(
    d =>
      d &&
      d.active !== false &&
      d.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  )
}

/** Visible Setup section tabs / create picker (29.70). */
export function foodSafetySetupVisibleSections(definitions = []) {
  if (hasFoodSafetyFridgeDefinition(definitions)) {
    return [...FOOD_SAFETY_SETUP_SECTIONS]
  }
  return [...FOOD_SAFETY_SETUP_FIRST_RUN_SECTIONS]
}

/**
 * Under Frequency in Setup: Daily is the due rhythm, not a single reading.
 * Null for sections without a schedule.
 */
export function foodSafetySetupFrequencyHint(sectionOrForm) {
  const section =
    typeof sectionOrForm === 'string' ? sectionOrForm : sectionOrForm?.section
  if (section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS) {
    return `Daily is the due rhythm, not one reading. Add due times below — each is its own check. Staff can still add extra readings (up to ${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY}). We'll remind once when a due time is missed.`
  }
  if (section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS) {
    if (isCleaningJob(sectionOrForm)) {
      return 'Frequency is how often this job is due. Staff sign it off once on each due day.'
    }
    return 'Frequency is when this checklist is due. Staff complete it once on each due day.'
  }
  return null
}

/**
 * Opening days. Weekdays are dayjs numbers: 0 Sunday … 6 Saturday.
 * Shown Monday first. Nothing saved means all seven are open.
 */
export const FOOD_SAFETY_OPENING_DAY_OPTIONS = Object.freeze([
  { value: 1, label: 'Mon', name: 'Monday' },
  { value: 2, label: 'Tue', name: 'Tuesday' },
  { value: 3, label: 'Wed', name: 'Wednesday' },
  { value: 4, label: 'Thu', name: 'Thursday' },
  { value: 5, label: 'Fri', name: 'Friday' },
  { value: 6, label: 'Sat', name: 'Saturday' },
  { value: 0, label: 'Sun', name: 'Sunday' },
])

export const FOOD_SAFETY_OPENING_WEEKDAYS_ALL = Object.freeze([
  0, 1, 2, 3, 4, 5, 6,
])

const openingDaySet = weekdays =>
  new Set(
    (Array.isArray(weekdays) ? weekdays : [])
      .map(Number)
      .filter(day => Number.isInteger(day) && day >= 0 && day <= 6),
  )

export const FOOD_SAFETY_OPENING_DAYS_UNSAVED_LINE =
  'Open every day until you save opening days.'

const FOOD_SAFETY_OPENING_DAYS_SAVED_INTRO =
  'Days the kitchen is open. All seven stay on until you save a change.'

/** True once a weekly pattern has been saved. */
export function openingDaysPatternSaved(settings) {
  return Boolean(String(settings?.operatingDaysEffectiveFrom || '').trim())
}

/** Card line. Unsaved venues stay open every day until Save days. */
export function formatOpeningDaysIntro(settings) {
  return openingDaysPatternSaved(settings)
    ? FOOD_SAFETY_OPENING_DAYS_SAVED_INTRO
    : FOOD_SAFETY_OPENING_DAYS_UNSAVED_LINE
}

/** Saved open days, or all seven when nothing has been saved. */
export function resolveOpeningWeekdays(settings) {
  const effectiveFrom = settings?.operatingDaysEffectiveFrom
  const raw = settings?.operatingWeekdays
  if (!effectiveFrom || !Array.isArray(raw) || raw.length === 0) {
    return [...FOOD_SAFETY_OPENING_WEEKDAYS_ALL]
  }
  const days = [...openingDaySet(raw)].sort((a, b) => a - b)
  return days.length ? days : [...FOOD_SAFETY_OPENING_WEEKDAYS_ALL]
}

export function openingWeekdaysEqual(left, right) {
  const key = weekdays =>
    [...openingDaySet(weekdays)].sort((a, b) => a - b).join(',')
  return key(left) === key(right)
}

export function toggleOpeningWeekday(weekdays, day) {
  const next = openingDaySet(weekdays)
  const value = Number(day)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return [...next].sort((a, b) => a - b)
}

/**
 * Names the closed days, Monday first.
 * “Monday and Tuesday won’t be marked missed.”
 * Empty when every day is open.
 */
export function formatOpeningDaysClosedLine(weekdays) {
  const open = openingDaySet(weekdays)
  if (open.size === 0) return 'Choose at least one open day.'
  const names = FOOD_SAFETY_OPENING_DAY_OPTIONS.filter(
    option => !open.has(option.value),
  ).map(option => option.name)
  if (!names.length) return ''
  const list =
    names.length === 1
      ? names[0]
      : names.length === 2
      ? `${names[0]} and ${names[1]}`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `${list} won’t be marked missed.`
}

dayjs.extend(customParseFormat)

export const FOOD_SAFETY_CLOSED_DATES_MAX = 40

export function isFoodSafetyClosedDate(value) {
  return typeof value === 'string' && dayjs(value, 'YYYY-MM-DD', true).isValid()
}

export function readFoodSafetyClosedDates(settings) {
  const raw = Array.isArray(settings) ? settings : settings?.closedDates
  if (!Array.isArray(raw)) return []
  return [...new Set(raw.filter(isFoodSafetyClosedDate))].sort()
}

export function formatFoodSafetyClosedDate(localDate) {
  if (!isFoodSafetyClosedDate(localDate)) return ''
  return dayjs(localDate, 'YYYY-MM-DD').format('D MMM YYYY')
}

export function foodSafetyClosedDatesAfterAdd(dates, localDate) {
  const current = readFoodSafetyClosedDates(dates)
  const date = String(localDate || '').trim()
  if (!isFoodSafetyClosedDate(date)) return { error: 'Choose a real date.' }
  if (current.includes(date)) return { error: 'That date is already closed.' }
  if (current.length >= FOOD_SAFETY_CLOSED_DATES_MAX) {
    return { error: 'Too many closed dates.' }
  }
  return { dates: [...current, date].sort() }
}

export function foodSafetyClosedDatesAfterRemove(dates, localDate) {
  return readFoodSafetyClosedDates(dates).filter(date => date !== localDate)
}

/** Setup hint when a check has no schedule. */
export function formatSetupNoScheduleHint(section, form) {
  if (isProbeSection(section)) {
    return isDishwasherUnit(form)
      ? 'No schedule for this dishwasher — test it when needed.'
      : 'No schedule for this probe — test it when needed.'
  }
  return 'No schedule for this section — staff log when needed.'
}
